import { OutcomesForParty, OutcomeSummary } from "./Analytics.ts";
import { Stage } from "./CaseConfiguration.ts";
import { Case, StoredResults } from "./CaseModel.ts";
import { AdjustedParty, AdjustedResults } from "./settlementAdjusted.ts";

/** An outcome as a page reads it; with settlements it may be one or stem from a world. */
export type ShownOutcome = OutcomeSummary & {
  // the party left the case by settlement at this stage
  settledAt?: Stage;
  world?: string;
};

/** A party's outcomes with the settlements the user expects, as pages read outcomes. */
export function adjustedOutcomes(
  litigation: OutcomesForParty<OutcomeSummary>,
  { outcomes, settlement, average }: AdjustedParty,
): OutcomesForParty<ShownOutcome> {
  const evOf = (stage: Stage) =>
    average.stageEvs.find((row) => row.stage === stage)?.ev;
  return {
    ...litigation,
    outcomes: [
      ...outcomes,
      ...(settlement?.points ?? []).map(
        ({ stage, probability, netResult, value, lawFirm, world }) => ({
          uuid: `settled-${world}`,
          scenario: { won: [], lost: [] },
          probability,
          issuesWon: [],
          issuesLost: [],
          value,
          netResult,
          // a settlement is paid as agreed: nothing to enforce, none short
          enforcementVariants: [
            {
              value,
              balance: value,
              costs: { generic: 0, perParty: {}, total: 0 },
              payments: {},
            },
          ],
          lawFirm,
          settledAt: stage,
          world,
        }),
      ),
    ],
    average: {
      ...litigation.average,
      party: average.party,
      barrister: average.barrister,
      lawFirm: average.lawFirm,
      stageResults: litigation.average.stageResults.map((row) => ({
        ...row,
        ev: { ...row.ev, party: evOf(row.stage) ?? row.ev.party },
      })),
    },
  };
}

/** The expected values, a party's own and those against each opponent. */
export function adjustedEvs(
  evs: StoredResults["evs"],
  parties: AdjustedParty[],
): StoredResults["evs"] {
  return Object.fromEntries(
    Object.entries(evs).map(([partyId, ofParty]) => {
      const party = parties.find((p) => p.partyId === partyId);
      if (!party) return [partyId, ofParty];
      return [
        partyId,
        {
          ...ofParty,
          own: ofParty.own.map((row) => ({
            ...row,
            value:
              party.average.stageEvs.find(({ stage }) => stage === row.stage)
                ?.ev ?? row.value,
          })),
          vsOthers: Object.fromEntries(
            Object.entries(ofParty.vsOthers).map(([opponentId, rows]) => [
              opponentId,
              rows.map((row) => ({
                ...row,
                value:
                  party.average.stageEvsVs[opponentId]?.find(
                    ({ stage }) => stage === row.stage,
                  )?.ev ?? row.value,
              })),
            ]),
          ),
        },
      ];
    }),
  );
}

const doneOf = (c: Case): AdjustedResults | undefined =>
  c.settlementAdjusted?.status === "done" ? c.settlementAdjusted : undefined;

/** The case as the analytics read it with the switch on adjusted: litigated where nothing is. */
export function adjustedCase(c: Case): Case {
  const adjusted = doneOf(c);
  if (!adjusted || !c.results) return c;
  const { parties } = adjusted;
  const of = (id: string) => parties.find(({ partyId }) => partyId === id);
  const { results } = c;
  return {
    ...c,
    results: {
      ...results,
      evs: adjustedEvs(results.evs, parties),
      outcomes: results.outcomes.map((outcome) => {
        const party = of(outcome.partyId);
        return party ? adjustedOutcomes(outcome, party) : outcome;
      }),
      partyStats: results.partyStats?.map((ofParty) => {
        const party = of(ofParty.partyId);
        return party
          ? {
              ...ofParty,
              stats: {
                ...ofParty.stats,
                ...party.stats,
                ev: party.average.stageEvs[0]?.ev ?? ofParty.stats.ev,
              },
            }
          : ofParty;
      }),
      ...(adjusted.revenueData && { revenueData: adjusted.revenueData }),
    },
  };
}

/** A party's net results in one view: the figures, and the outcomes they come from. */
export type NetResultsOf = {
  stats: {
    netResult: { average: number; worst?: number; best?: number };
  };
  outcomes: { netResult: number; probability: number }[];
};

/** How likely a net result is: of the outcomes with it, to the cent. */
const chanceOf = (outcomes: NetResultsOf["outcomes"], netResult: number) =>
  outcomes
    .filter((outcome) => Math.abs(outcome.netResult - netResult) < 0.005)
    .reduce((sum, { probability }) => sum + probability, 0);

/** How likely a party is to be left with more than nothing. */
export const chanceOfPositive = (outcomes: NetResultsOf["outcomes"]) =>
  outcomes
    .filter(({ netResult }) => netResult >= 0.005)
    .reduce((sum, { probability }) => sum + probability, 0);

/**
 * What settling does to a party's net result, against litigation: how far it
 * moves the average, the worst and the best, and how likely the worst, the
 * best and a positive net result are with and without it. A settlement
 * leaves the outcomes at judgment possible, only less likely, so the worst
 * and the best mostly stay where they are and grow less likely.
 */
export function netResultComparison(
  litigation: NetResultsOf,
  adjusted: NetResultsOf,
) {
  const end = (of: NetResultsOf, which: "worst" | "best") =>
    of.stats.netResult[which] ?? of.stats.netResult.average;
  const compared = (which: "worst" | "best") => ({
    change: end(adjusted, which) - end(litigation, which),
    chance: chanceOf(adjusted.outcomes, end(adjusted, which)),
    without: chanceOf(litigation.outcomes, end(litigation, which)),
  });
  return {
    average:
      adjusted.stats.netResult.average - litigation.stats.netResult.average,
    worst: compared("worst"),
    best: compared("best"),
    positive: {
      chance: chanceOfPositive(adjusted.outcomes),
      without: chanceOfPositive(litigation.outcomes),
    },
  };
}

/** How far a party has settled with an opponent by a stage, and settles in it. */
export type SettlingAt = { opponentId: string; byThen: number; here: number };

/**
 * How far a party has settled with each opponent at a stage, seen from now:
 * the chance it has settled with them before the stage begins, and of
 * settling with them in it. A settlement comes at the start of its stage.
 */
export function settlingAt(
  pairs: AdjustedResults["pairs"],
  partyId: string,
  stage: Stage,
): SettlingAt[] {
  return pairs.flatMap(({ parties, stages }) => {
    if (!parties.includes(partyId)) return [];
    const opponentId = parties[0] === partyId ? parties[1] : parties[0];
    const before = stages.filter((row) => row.stage < stage);
    return [
      {
        opponentId,
        byThen: before.reduce((sum, { here }) => sum + here, 0),
        here: stages.find((row) => row.stage === stage)?.here ?? 0,
      },
    ];
  });
}
